/* istanbul ignore file */
import { createAction } from '@reduxjs/toolkit';
import type {
    IAnonymousAppointment,
    IAppointment,
    IAppointmentBranch,
    IAppointmentSettings,
    IContactRequest,
    ICustomerBase,
    IFetchRmTimeslotsRequest,
    IFetchTimeslotsRequest,
    IRule,
    ITimeslot,
    ITopic,
} from '../../api/domain';

enum AppointmentsApiActions {
    BOOK_APPOINTMENT = 'appointments.api.book.appointment',
    BOOK_APPOINTMENT_ANONYMOUS = 'appointments.api.book.appointment-anonymous',
    BOOK_APPOINTMENT_ANONYMOUS_FAILURE = 'appointments.api.book.appointment-anonymous.failure',
    BOOK_APPOINTMENT_ANONYMOUS_SUCCESS = 'appointments.api.book.appointment-anonymous.success',
    BOOK_APPOINTMENT_FAILURE = 'appointments.api.book.appointment.failure',
    BOOK_APPOINTMENT_SUCCESS = 'appointments.api.book.appointment.success',
    CLEAR_BOOKED_APPOINTMENT = 'appointment.api.clear.appointment',
    DOWNLOAD_ICAL = 'appointments.api.download.ical',
    DOWNLOAD_ICAL_FAILURE = 'appointments.api.download.ical.failure',
    DOWNLOAD_ICAL_SUCCESS = 'appointments.api.download.ical.success',
    FETCH_ALL_RM_TIMESLOTS_FOR_TOPIC = 'appointments.api.fetch.all.rm.topic.timeslots',
    FETCH_BRANCHES = 'appointments.api.fetch.branches',
    FETCH_BRANCHES_FAILURE = 'appointments.api.fetch.branches.failure',
    FETCH_BRANCHES_SUCCESS = 'appointments.api.fetch.branches.success',
    FETCH_RM_TIMESLOTS = 'appointments.api.fetch.rm.timeslots',
    FETCH_RM_TIMESLOTS_FAILURE = 'appointments.api.fetch.rm.timeslots.failure',
    FETCH_RM_TIMESLOTS_SUCCESS = 'appointments.api.fetch.rm.timeslots.success',
    FETCH_RULES = 'appointments.api.fetch.rules',
    FETCH_RULES_FAILURE = 'appointments.api.fetch.rules.failure',
    FETCH_RULES_SUCCESS = 'appointments.api.fetch.rules.success',
    FETCH_SETTINGS = 'appointments.api.fetch.settings',
    FETCH_SETTINGS_FAILURE = 'appointments.api.fetch.settings.failure',
    FETCH_SETTINGS_SUCCESS = 'appointments.api.fetch.settings.success',
    FETCH_TIMESLOTS = 'appointments.api.fetch.timeslots',
    FETCH_TIMESLOTS_FAILURE = 'appointments.api.fetch.timeslots.failure',
    FETCH_TIMESLOTS_SUCCESS = 'appointments.api.fetch.timeslots.success',
    FETCH_TOPICS = 'appointments.api.fetch.topics',
    FETCH_TOPICS_FAILURE = 'appointments.api.fetch.topics.failure',
    FETCH_TOPICS_SUCCESS = 'appointments.api.fetch.topics.success',
    SELECT_CUSTOMER_BASE_FOR_APPOINTMENT = 'appointments.api.select-customer-base',
    SEND_CONTACT_REQUEST = 'appointments.api.send-contact-request',
    SEND_CONTACT_REQUEST_FAILURE = 'appointments.api.send-contact-request.failure',
    SEND_CONTACT_REQUEST_SUCCESS = 'appointments.api.send-contact-request.success',
}

export const selectCustomerBaseForAppointment = createAction<ICustomerBase | undefined>(AppointmentsApiActions.SELECT_CUSTOMER_BASE_FOR_APPOINTMENT);

export const downloadICal = createAction<{
    appointmentId: string;
    anonymousCustomerId?: string;
}>(AppointmentsApiActions.DOWNLOAD_ICAL);
export const bookAppointment = createAction<IAppointment>(AppointmentsApiActions.BOOK_APPOINTMENT);
export const bookAppointmentAnonymous = createAction<IAnonymousAppointment>(AppointmentsApiActions.BOOK_APPOINTMENT_ANONYMOUS);
export const bookAppointmentAnonymousFailure = createAction(AppointmentsApiActions.BOOK_APPOINTMENT_ANONYMOUS_FAILURE);
export const bookAppointmentAnonymousSuccess = createAction<IAnonymousAppointment>(AppointmentsApiActions.BOOK_APPOINTMENT_ANONYMOUS_SUCCESS);
export const bookAppointmentFailure = createAction(AppointmentsApiActions.BOOK_APPOINTMENT_FAILURE);
export const bookAppointmentSuccess = createAction<IAppointment>(AppointmentsApiActions.BOOK_APPOINTMENT_SUCCESS);
export const clearBookedAppointment = createAction(AppointmentsApiActions.CLEAR_BOOKED_APPOINTMENT);
export const downloadICalFailure = createAction(AppointmentsApiActions.DOWNLOAD_ICAL_FAILURE);
export const downloadICalSuccess = createAction(AppointmentsApiActions.DOWNLOAD_ICAL_SUCCESS);
export const fetchAllRelationshipManagerTimeSlotsForTopic = createAction<string>(AppointmentsApiActions.FETCH_ALL_RM_TIMESLOTS_FOR_TOPIC);
export const fetchBranches = createAction(AppointmentsApiActions.FETCH_BRANCHES);
export const fetchBranchesFailure = createAction(AppointmentsApiActions.FETCH_BRANCHES_FAILURE);
export const fetchBranchesSuccess = createAction<IAppointmentBranch[]>(AppointmentsApiActions.FETCH_BRANCHES_SUCCESS);
export const fetchRelationshipManagerTimeslots = createAction<IFetchRmTimeslotsRequest & IFetchTimeslotsRequest & { externalId: string }>(
    AppointmentsApiActions.FETCH_RM_TIMESLOTS,
);
export const fetchRelationshipManagerTimeslotsFailure = createAction(AppointmentsApiActions.FETCH_RM_TIMESLOTS_FAILURE);
export const fetchRelationshipManagerTimeslotsSuccess = createAction<ITimeslot[]>(AppointmentsApiActions.FETCH_RM_TIMESLOTS_SUCCESS);
export const fetchRules = createAction(AppointmentsApiActions.FETCH_RULES);
export const fetchRulesFailure = createAction(AppointmentsApiActions.FETCH_RULES_FAILURE);
export const fetchRulesSuccess = createAction<IRule[]>(AppointmentsApiActions.FETCH_RULES_SUCCESS);
export const fetchSettings = createAction(AppointmentsApiActions.FETCH_SETTINGS);
export const fetchSettingsFailure = createAction(AppointmentsApiActions.FETCH_SETTINGS_FAILURE);
export const fetchSettingsSuccess = createAction<IAppointmentSettings>(AppointmentsApiActions.FETCH_SETTINGS_SUCCESS);
export const fetchTimeslots = createAction<IFetchTimeslotsRequest>(AppointmentsApiActions.FETCH_TIMESLOTS);
export const fetchTimeslotsFailure = createAction(AppointmentsApiActions.FETCH_TIMESLOTS_FAILURE);
export const fetchTimeslotsSuccess = createAction<ITimeslot[]>(AppointmentsApiActions.FETCH_TIMESLOTS_SUCCESS);
export const fetchTopics = createAction(AppointmentsApiActions.FETCH_TOPICS);
export const fetchTopicsFailure = createAction(AppointmentsApiActions.FETCH_TOPICS_FAILURE);
export const fetchTopicsSuccess = createAction<ITopic[]>(AppointmentsApiActions.FETCH_TOPICS_SUCCESS);
export const sendContactRequest = createAction<IContactRequest>(AppointmentsApiActions.SEND_CONTACT_REQUEST);
export const sendContactRequestFailure = createAction(AppointmentsApiActions.SEND_CONTACT_REQUEST_FAILURE);
export const sendContactRequestSuccess = createAction(AppointmentsApiActions.SEND_CONTACT_REQUEST_SUCCESS);
